/** The battle screen's palette and its painted-panel primitives.
 *
 * The classic's chrome is carved wood, cut stone and gilt, not a flat box with a
 * radius. There is no panel art in the atlas, so the panels are drawn: a wood
 * ground, a plank seam, a bevelled inner edge lit from the top-left, a gilt
 * frame with a dark keyline, and rivets at the corners. Every value here is a
 * constant so two panels drawn a frame apart are identical.
 *
 * Two rules keep it from sliding back into flat CSS boxes:
 *
 *   - **No face is one flat fill.** Every stone or wood surface gets `grain`
 *     over it: a deterministic speckle plus a vertical value ramp. A uniform
 *     `#9a9a9a` rectangle with a 1px stroke is what a placeholder looks like.
 *   - **No neutral grey.** The frame is brown and gilt, so the stone is a warm
 *     grey-brown. A genuinely grey plate in a brown frame reads as unpainted.
 */

import type { Graphics } from "pixi.js";

export const COLOR = {
  woodDark: 0x241a10,
  wood: 0x3d2a18,
  woodLit: 0x5c412a,
  woodSeam: 0x1a120a,
  /** Warm quarried stone. Neutral grey in a gilt-and-brown frame reads unpainted. */
  stone: 0x5f5546,
  stoneLit: 0x968a74,
  stoneDark: 0x3f382e,
  stoneShadow: 0x241f19,
  giltLit: 0xe8cf7a,
  gilt: 0xc9a227,
  giltDark: 0x7d5f12,
  parchment: 0xf3e6c4,
  ink: 0x2a1d0d,
  hpBar: 0x54b24a,
  hpBarLow: 0xc23b2a,
  // There is deliberately no `reach` fill colour here any more. It was
  // 0xffc25c, a warm gold laid over the reachable region at alpha 0.26 - which
  // composites to `0.74L + 52.2`, a lift of +16 to +29 luma over a backdrop
  // whose own tile means span 111.6. The movement marking is a *darkening* now,
  // by a multiply rather than by any colour (`REACH_SHADE` in `ui.ts`), because
  // a multiply is a fixed ratio of the ground and an additive wash is not. Do
  // not reintroduce a lit fill for it. It falls on the ground the stack cannot
  // reach rather than on the ground it can; `reachShadeCells` in `ui.ts` carries
  // the measurement that settles which way round it goes.
  /** The hex outline on a reachable cell, and the lit half of the path arrows. */
  reachEdge: 0xffeab4,
  // There is deliberately no `gridLine` here any more either. It was 0xd0c060,
  // carrying a citation to an expansion reference file that is not on disk, and by
  // 2026-08-30 no code read it at all: the grid line is `gridLineColor` in
  // `ui.ts`, solved per arena off that arena's own plate, and no single constant
  // here could stand in for it. Two names for one line, one of them dead and mis-cited, is worse than
  // none. Grep found the dead one referenced only by prose.
  attack: 0xff6a3d,
  cast: 0x8ad4ff,
  shadow: 0x0b0a08,
} as const;

/** The classic's eight player colours, used to tint the stack-count badges. */
export const PLAYER_COLOR: Readonly<Record<string, number>> = {
  red: 0xc8322a, blue: 0x2f5bc4, tan: 0xa98652, green: 0x2f8b3d,
  orange: 0xdd8420, purple: 0x8a3fbf, teal: 0x1f9c9c, pink: 0xd96fae,
};

/** Attacker left, defender right, when the app has no player colours to give. */
export const SIDE_COLOR: readonly [number, number] = [PLAYER_COLOR["red"]!, PLAYER_COLOR["blue"]!];

/**
 * There is no vector face on this screen any more, and that is the point.
 *
 * `FONT_STACK` stood here - Georgia, falling back to Times New Roman - and the
 * two largest strings in the frame were set in it: the console's narration line
 * and both commanders' names. Measured on the shipped 1920x1080 shot, "Round 1
 * - Your move" carried 1,452 distinct colours against 16 in "ATTACKER" 700
 * pixels away, and both were drawn through the arena's 1.5x magnification, so
 * no stem landed on a whole device pixel. A hinted serif resampled by 1.5 is
 * the single loudest "this is a web page" tell a pixel-art screen can carry.
 *
 * Everything here is a `BitmapLabel` now (`packages/render/src/text`), which is
 * axis-aligned rectangles at a whole magnification and exactly the colours it
 * was given. The three faces are cap 7, 9 and 11; a call site picks one and a
 * whole scale, and gets the same rasterisation in every plaque on the frame.
 */

/** Deterministic 32-bit noise, so masonry and scatter are identical every frame. */
export function noise(seed: number, salt: number): number {
  let h = (seed * 374761393 + salt * 668265263) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = (h * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export function mixColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  return (Math.round(ar + (br - ar) * t) << 16)
    | (Math.round(ag + (bg - ag) * t) << 8)
    | Math.round(ab + (bb - ab) * t);
}

/**
 * Speckle and a light ramp over a rectangle: the difference between a painted
 * material and a filled box. `seed` keys the speckle so the same plate is the
 * same stone on every frame, and the ramp is what gives the face a direction of
 * light rather than a single value.
 */
export function grain(
  g: Graphics, x: number, y: number, w: number, h: number, seed: number,
  strength = 1,
): void {
  const rows = Math.max(4, Math.round(h / 3));
  for (let i = 0; i < rows; i++) {
    const k = i / rows;
    // A ramp from lit at the top to shaded at the bottom, one light source.
    const a = (0.5 - k) * 0.16 * strength;
    g.rect(x, y + k * h, w, h / rows + 0.6)
      .fill({ color: a > 0 ? 0xffffff : 0x000000, alpha: Math.abs(a) });
  }
  const specks = Math.max(6, Math.round((w * h) / 46));
  for (let i = 0; i < specks; i++) {
    const sx = x + noise(seed, i * 3 + 1) * w;
    const sy = y + noise(seed, i * 3 + 2) * h;
    const v = noise(seed, i * 3 + 3);
    const r = 0.6 + v * 1.5;
    g.ellipse(sx, sy, r, r * 0.8)
      .fill({ color: v > 0.5 ? 0xffffff : 0x000000, alpha: (0.05 + v * 0.09) * strength });
  }
}

// ------------------------------------------------------------------ chamfer

/**
 * How deep every cut corner on this screen is, in arena pixels.
 *
 * The classic has no square corner anywhere in its frame: every plate, plaque, button
 * and portrait socket has its corners taken off at 45 degrees, and the cut is
 * lit on the two upper faces and shadowed on the two lower ones. A right-angled
 * corner on a gilt frame is the single cheapest tell that a panel was specified
 * in CSS rather than carved, and this screen had a right angle on all four
 * corners of every one of its panels.
 */
export const CHAMFER = 4;

/** A rectangle with its corners cut off: the outline every panel here is built on. */
export function chamfered(x: number, y: number, w: number, h: number, cut: number): number[] {
  const c = Math.max(0, Math.min(cut, Math.min(w, h) / 2));
  if (c <= 0) return [x, y, x + w, y, x + w, y + h, x, y + h];
  return [
    x + c, y, x + w - c, y, x + w, y + c, x + w, y + h - c,
    x + w - c, y + h, x + c, y + h, x, y + h - c, x, y + c,
  ];
}

/**
 * Take the corners off a panel that was painted square.
 *
 * The bevels, grain and speckle inside a panel are drawn as full-width rows, so
 * they run into the corner whatever shape the base fill is. This paints the four
 * corner triangles back out in the frame's own keyline colour and then catches
 * the light on the two upper cuts, which is what turns a notch into a chamfer.
 */
export function cutCorners(
  g: Graphics, w: number, h: number, cut = CHAMFER, keyline = 0x120c07,
): void {
  const c = Math.max(1, Math.min(cut, Math.min(w, h) / 2));
  g.poly([0, 0, c, 0, 0, c]).fill({ color: keyline });
  g.poly([w, 0, w - c, 0, w, c]).fill({ color: keyline });
  g.poly([0, h, c, h, 0, h - c]).fill({ color: keyline });
  g.poly([w, h, w - c, h, w, h - c]).fill({ color: keyline });
  // One light source, upper left: the two upper cuts catch it, the lower two
  // are the shadowed side of the same bevel.
  g.moveTo(0.5, c).lineTo(c, 0.5).stroke({ width: 1, color: COLOR.giltLit, alpha: 0.55 });
  g.moveTo(w - c, 0.5).lineTo(w - 0.5, c).stroke({ width: 1, color: COLOR.gilt, alpha: 0.45 });
  g.moveTo(0.5, h - c).lineTo(c, h - 0.5).stroke({ width: 1, color: 0x000000, alpha: 0.5 });
  g.moveTo(w - c, h - 0.5).lineTo(w - 0.5, h - c).stroke({ width: 1, color: 0x000000, alpha: 0.6 });
}

/**
 * A carved-wood panel with a gilt frame. `g` is cleared first, so a panel can be
 * resized in place without accumulating geometry.
 */
export function paintPanel(g: Graphics, w: number, h: number): Graphics {
  g.clear();
  // Drop shadow, so the panel sits above the battlefield rather than on it.
  g.rect(3, 4, w, h).fill({ color: COLOR.shadow, alpha: 0.45 });
  // Gilt frame and its keyline.
  g.rect(0, 0, w, h).fill({ color: COLOR.giltDark });
  g.rect(1, 1, w - 2, h - 2).fill({ color: COLOR.gilt });
  g.rect(2, 2, w - 4, h - 4).fill({ color: COLOR.giltDark });
  // Wood ground.
  g.rect(4, 4, w - 8, h - 8).fill({ color: COLOR.wood });
  woodGrain(g, 4, 4, w - 8, h - 8, 17);
  // Lit top-left bevel, shaded bottom-right: one light source, upper left.
  g.rect(4, 4, w - 8, 2).fill({ color: COLOR.woodLit, alpha: 0.55 });
  g.rect(4, 4, 2, h - 8).fill({ color: COLOR.woodLit, alpha: 0.4 });
  g.rect(4, h - 6, w - 8, 2).fill({ color: COLOR.woodDark, alpha: 0.7 });
  g.rect(w - 6, 4, 2, h - 8).fill({ color: COLOR.woodDark, alpha: 0.6 });
  // Rivets.
  for (const [rx, ry] of [[7, 7], [w - 8, 7], [7, h - 8], [w - 8, h - 8]] as const) {
    g.circle(rx, ry, 2.2).fill({ color: COLOR.giltDark });
    g.circle(rx - 0.5, ry - 0.5, 1.2).fill({ color: COLOR.giltLit });
  }
  cutCorners(g, w, h, CHAMFER + 1);
  return g;
}

/** Long horizontal streaks with the odd knot: sawn plank, not a brown fill. */
export function woodGrain(g: Graphics, x: number, y: number, w: number, h: number, seed: number): void {
  const lines = Math.max(3, Math.round(h / 4));
  for (let i = 0; i < lines; i++) {
    const ly = y + ((i + 0.5) / lines) * h;
    const v = noise(seed, i * 5 + 1);
    const x0 = x + noise(seed, i * 5 + 2) * w * 0.3;
    const x1 = x + w - noise(seed, i * 5 + 3) * w * 0.3;
    g.rect(x0, ly, x1 - x0, 0.8 + v * 0.9)
      .fill({ color: v > 0.55 ? COLOR.woodLit : COLOR.woodSeam, alpha: 0.1 + v * 0.16 });
  }
  for (let i = 0; i < Math.max(1, Math.round(w / 90)); i++) {
    const kx = x + noise(seed, i * 11 + 7) * w;
    const ky = y + noise(seed, i * 11 + 8) * h;
    g.ellipse(kx, ky, 3.2, 1.6).fill({ color: COLOR.woodSeam, alpha: 0.3 });
    g.ellipse(kx, ky, 1.6, 0.8).fill({ color: COLOR.woodLit, alpha: 0.16 });
  }
}

/**
 * A cut-stone plate for the initiative bar: a gilt-edged block of warm stone
 * with a **dark recess** cut into it for the portrait, and the owner's colour
 * carried as a tinted band across the plate rather than as a neon stroke around
 * it. A 2px pure-red rectangle around a flat grey square is a CSS swatch; the
 * recess is what a dark unit needs to be readable at 40px.
 */
export function paintStonePlate(g: Graphics, w: number, h: number, tint: number, seed = 3): Graphics {
  g.clear();
  g.rect(1, 2, w, h).fill({ color: COLOR.shadow, alpha: 0.5 });
  // Outer keyline and gilt bezel.
  g.rect(0, 0, w, h).fill({ color: 0x120d07 });
  g.rect(1, 1, w - 2, h - 2).fill({ color: COLOR.giltDark });
  // Stone face, tinted toward the owner's colour so the side reads at a glance.
  // 0.44 of the owner's colour, not 0.34. The rail is read down its length as
  // two interleaved sequences - whose stack acts next - and at 0.34 the two
  // faces were (127,74,62) and (81,83,113), a hue apart but only 9 luma and 30
  // chroma, which is not a sort at 40 pixels. At 0.44 they are (137,63,55) and
  // (72,80,124): the same two hues, half again the separation.
  const face = mixColor(COLOR.stone, tint, 0.44);
  g.rect(2, 2, w - 4, h - 4).fill({ color: face });
  grain(g, 2, 2, w - 4, h - 4, seed, 0.9);
  g.rect(2, 2, w - 4, 1.5).fill({ color: COLOR.stoneLit, alpha: 0.6 });
  g.rect(2, 2, 1.5, h - 4).fill({ color: COLOR.stoneLit, alpha: 0.35 });
  g.rect(2, h - 3.5, w - 4, 1.5).fill({ color: COLOR.stoneShadow, alpha: 0.85 });
  g.rect(w - 3.5, 2, 1.5, h - 4).fill({ color: COLOR.stoneShadow, alpha: 0.7 });
  cutCorners(g, w, h, CHAMFER, 0x120d07);
  return g;
}

/**
 * The niche a queue portrait stands in - **lit**, not a black hole.
 *
 * It used to be a 0x0f0b07 fill with a cool wash over it at falling alpha,
 * which is a mean luma of about 20. Two thirds of this game's creatures are
 * dark - skeletons, black knights, wraiths, dread knights - so a dark sprite in
 * a dark niche is not a silhouette, it is nothing. Measured on the shipped
 * 1920x1080 shot before this change, fourteen of the fifteen queue recesses had
 * a mean luma under 40 against a battlefield at 89, and a critic reported ten of
 * the fifteen as unreadable dark rectangles.
 *
 * A recess does not have to be dark to read as a recess: what says "cut into
 * the plate" is the hard black keyline along its top and left and the lit lip
 * along its foot, which are still here. The wall itself is a lit alcove - cool
 * daylight at the head, warm ground at the foot - so **whatever** stands in it
 * silhouettes. The classic's own queue plates do the same thing and go further: their
 * creature icons carry a painted sky-and-landscape background, which is why a
 * umbral wyrm reads at 40 pixels there.
 *
 * The owner's colour is carried at 0.18 rather than 0.30, because it is now
 * tinting a light wall instead of a dark one and the same fraction of a bright
 * base is a much louder colour.
 */
export function paintInset(g: Graphics, x: number, y: number, w: number, h: number, tint: number): void {
  // The alcove wall carries the owner's colour harder than it did (0.30/0.24
  // against 0.18/0.14) for the same reason `paintStonePlate` does: the niche is
  // the largest coloured area on a queue plate, and it is what tells the two
  // sides apart down the length of the rail.
  const head = mixColor(0x5f6b78, tint, 0.30);
  const foot = mixColor(0x6d5f47, tint, 0.24);
  const bands = 10;
  for (let i = 0; i < bands; i++) {
    const k = i / (bands - 1);
    g.rect(x, y + (h * i) / bands, w, h / bands + 0.6)
      .fill({ color: mixColor(head, foot, k) });
  }
  grain(g, x, y, w, h, 29, 0.5);
  // The alcove is deeper than it is wide, so its own walls shade its edges. Four
  // flat steps rather than a gradient, like every other shadow on this screen.
  for (let i = 0; i < 4; i++) {
    const a = 0.16 * (1 - i / 4);
    g.rect(x + i, y, 1, h).fill({ color: 0x000000, alpha: a });
    g.rect(x + w - 1 - i, y, 1, h).fill({ color: 0x000000, alpha: a * 1.4 });
    g.rect(x, y + i, w, 1).fill({ color: 0x000000, alpha: a * 0.8 });
  }
  // The cut: hard black along the top and left, a lit lip along the foot.
  g.rect(x, y, w, 1).fill({ color: 0x000000, alpha: 0.85 });
  g.rect(x, y, 1, h).fill({ color: 0x000000, alpha: 0.7 });
  g.rect(x + w - 1, y, 1, h).fill({ color: 0x000000, alpha: 0.45 });
  g.rect(x, y + h - 1, w, 1).fill({ color: COLOR.stoneLit, alpha: 0.45 });
}

/**
 * The **join** where a figure meets the ground - not its shadow.
 *
 * The shadow is the projected silhouette (`SHADOW_ALPHA` in `stacks.ts`,
 * `OBSTACLE_SHADOW_ALPHA` in `arena.ts`). This ellipse exists only because a
 * projection has a hard pixel edge and gaps between a creature's legs, so
 * something has to sit under the feet for them to sit *in*. The classic has no
 * ellipse at all: over the 77 exported `Standing` DEFs the entire shadow is the
 * silhouette, at two flat alphas and nothing else.
 *
 * It is drawn the way the rest of the screen is drawn: **stepped**, not
 * feathered. A gaussian smudge under one creature in an otherwise
 * nearest-neighbour scene is the single most obvious "this was composited by a
 * different program" tell, and the old ramp of eight ellipses at falling alpha
 * produced exactly that.
 *
 * Four flat rings is still the right shape; what was wrong was the depth. At
 * `strength` 0.75 the four coverages compose to **0.70 black** at the centre,
 * and measured layer by layer on the composited 1080p frame that made the
 * ellipse - not the silhouette - the darkest thing on the battlefield: over
 * `battle`, `battle-snow` and `battle-lava` the ellipse layer's deepest pixel
 * was 139.3 luma against the projection's 69.0, and 66-74% of the pixels it
 * darkened lay outside the projection's own footprint, where an ellipse is all
 * anyone can see. Both blind judges named "soft blurred elliptical drop shadow"
 * a top-two tell of this screen, and that was it.
 *
 * So callers now pass the clamp's own floor, 0.25, which composes to 0.30 - by
 * construction lighter than either projection alpha, so the darkest shape on
 * the ground is silhouette-shaped. `battle-shadow-depth.spec.ts` pins that
 * ordering by driving this function rather than by restating the numbers.
 *
 * Re-measured the same way after the change, over the same three scenes: the
 * stack ellipse layer went from 38,984 darkened pixels at a mean of 41.6 luma
 * and a deepest 139.3, to 8,259 at 17.0 and 58.6 - against a projection whose
 * deepest is now 108.0. Where it still shows past the silhouette it is a third
 * of the depth of the silhouette rather than twice it (ratio 1.88-2.02 before,
 * 0.36-0.54 after), and it carries 5.8% of the stacks' shadow ink where it
 * used to carry 55%.
 */
export function paintContactShadow(g: Graphics, rx: number, ry: number, strength = 0.5): Graphics {
  g.clear();
  const k = Math.max(0.25, Math.min(1.6, strength));
  // Outer, mid, core, and a dark seam right under the feet. Flat alphas: no ramp.
  g.ellipse(0, 0, rx, ry).fill({ color: 0x2a1c0a, alpha: 0.3 * k });
  g.ellipse(0, 0, rx * 0.78, ry * 0.8).fill({ color: 0x211505, alpha: 0.32 * k });
  g.ellipse(0, 0, rx * 0.52, ry * 0.62).fill({ color: 0x180f04, alpha: 0.36 * k });
  g.ellipse(0, -ry * 0.12, rx * 0.3, ry * 0.36).fill({ color: 0x120a03, alpha: 0.4 * k });
  return g;
}

// ------------------------------------------------------------ command bar

/**
 * The console band the command buttons sit in: hammered dark metal over wood,
 * with a gilt lip along the top and rivets at both ends. It spans the whole
 * width of the window, so it is drawn from `x` rather than from zero.
 */
export function paintCommandBand(g: Graphics, x: number, w: number, h: number): Graphics {
  g.clear();
  // A shadow cast up onto the battlefield, so the band sits in front of it.
  for (let i = 0; i < 10; i++) {
    g.rect(x, -i * 1.6 - 1.6, w, 1.6).fill({ color: 0x000000, alpha: 0.07 * (1 - i / 10) });
  }
  g.rect(x, 0, w, h).fill({ color: COLOR.woodDark });
  g.rect(x, 5, w, h - 9).fill({ color: COLOR.wood });
  woodGrain(g, x, 5, w, h - 9, 91);
  // Hammered metal: horizontal bands of slightly different value.
  for (let i = 0; i < 11; i++) {
    const k = i / 11;
    g.rect(x, 5 + (h - 9) * k, w, (h - 9) / 11)
      .fill({ color: 0x000000, alpha: 0.035 + Math.abs(k - 0.3) * 0.13 });
  }
  // Gilt lip, with thickness: a bright top face, a warm body, a dark underside.
  g.rect(x, 0, w, 5).fill({ color: COLOR.giltDark });
  g.rect(x, 0, w, 2).fill({ color: COLOR.gilt });
  g.rect(x, 0, w, 1).fill({ color: COLOR.giltLit });
  g.rect(x, 4.2, w, 1.6).fill({ color: 0x000000, alpha: 0.55 });
  // Bottom lip, so the band has two edges rather than running off the window.
  g.rect(x, h - 3, w, 3).fill({ color: 0x000000, alpha: 0.55 });
  g.rect(x, h - 3.5, w, 1).fill({ color: COLOR.giltDark, alpha: 0.7 });
  // Bolts along the lip, the way a riveted plate is fixed to its frame.
  for (let bx = x + 16; bx < x + w - 10; bx += 58) {
    g.circle(bx, h - 11, 3.4).fill({ color: 0x0e0a06, alpha: 0.75 });
    g.circle(bx, h - 12, 2.6).fill({ color: COLOR.giltDark });
    g.circle(bx - 0.8, h - 12.8, 1.2).fill({ color: COLOR.giltLit, alpha: 0.85 });
  }
  return g;
}

/** A recessed slot for text: the console's own reading window. */
export function paintRecess(g: Graphics, w: number, h: number): Graphics {
  g.clear();
  g.rect(0, 0, w, h).fill({ color: 0x120c07 });
  // Parchment tucked into the recess, so the slot is a material and not a hole.
  g.rect(2, 2, w - 4, h - 4).fill({ color: 0x2a1d0f });
  grain(g, 2, 2, w - 4, h - 4, 53, 0.7);
  for (let i = 0; i < 5; i++) {
    g.rect(2, 2 + i, w - 4, 1).fill({ color: 0x000000, alpha: 0.16 * (1 - i / 5) });
  }
  g.rect(0, 0, w, 1.5).fill({ color: 0x000000, alpha: 0.75 });
  g.rect(0, h - 1.5, w, 1.5).fill({ color: COLOR.woodLit, alpha: 0.4 });
  g.rect(0, 0, 1.5, h).fill({ color: 0x000000, alpha: 0.65 });
  g.rect(w - 1.5, 0, 1.5, h).fill({ color: COLOR.woodLit, alpha: 0.25 });
  g.rect(0, 0, w, h).stroke({ width: 1, color: COLOR.giltDark, alpha: 0.75, alignment: 0 });
  cutCorners(g, w, h, 3, 0x120c07);
  return g;
}

/**
 * The console's narration strip: the single most recognisable object on the classic's
 * battle screen.
 *
 * **The file this used to cite, a reference screenshot,
 * is not on disk and never was in this reference set.** Its two checkable
 * claims were checked on 2026-08-30 against the six real battle frames we do
 * have, and both came out differently.
 *
 *   - **The console is 44px, not 42.** `Siege_combat.png` is exactly 800x556,
 *     which is the field viewport, so the band below it is 600-556. Confirmed
 *     independently by the gilt lip row, which lands at native y 554.3
 *     (`CLASSIC_Battlefield.jpg`), 558.3 (a reference screenshot), 558.3 (a reference screenshot)
 *     and 557.0 (a reference screenshot) - all four within a rescale's error of 556.
 *
 *   - **The keyline is not crimson on any frame we have.** Hunting the console
 *     band for pixels that break the gilt ordering (r>g>b) finds 8,611 violet
 *     ones on a reference screenshot (dark quartile 55,11,65, light quartile
 *     150,96,162) and 1,319 teal ones on a reference screenshot (23,43,36 to
 *     159,176,168); `CLASSIC_Battlefield.jpg`, a reference screenshot, a reference screenshot and
 *     a reference screenshot have four such pixels between them, i.e. a plain gilt
 *     keyline. Red appears on none of the six.
 *
 * What the keyline is keyed to is **not** settled by these frames, and the
 * obvious guess is wrong: both grid frames carry the same violet count plates
 * (93,32,145 on classic16, 85,31,141 on classic11) while their keylines are purple and
 * teal respectively, so it is not simply the owning player's colour. Two of six
 * frames do have a saturated non-gilt keyline, which is the part of the original
 * argument that survives: a gilt-framed recess in a gilt-framed bar disappears
 * into it, and something has to separate them.
 *
 * **The crimson is gone.** It was ours rather than a measurement - kept on the
 * argument above - and what it cost was named from the other end: it was the
 * only saturated red in the frame, the same salmon the stack plaques carry, so
 * the calmest object on the screen ("Round 1 - your move", 1,200px wide, in the
 * middle of the bar) wore the alert colour and the damage floaters had nothing
 * left that was theirs. Red on this screen means a stack lost hit points.
 *
 * The separation the argument asks for is still drawn, in the material the rest
 * of the frame is made of: four rows of relief - dark keyline, gilt body, lit
 * top row, dark underside - which is the same bead `paintPanel` and
 * `paintStoneButton` put round every other opening here. Four of the six
 * reference frames read exactly that (a plain gilt keyline); the two that do not
 * disagree with each other about what theirs is keyed to.
 */
/**
 * The message panel's crimson rule and its reading surface, both measured off
 * a reference screenshot: the rule at (124,22,35)..(133,13,23) down the
 * inside of its bevel, the field at (58,37,20).
 */
const MESSAGE_RULE = 0x7d1622;
const MESSAGE_FIELD = 0x241708;

export function paintMessageStrip(g: Graphics, w: number, h: number): Graphics {
  g.clear();
  // The bar's own shadow around the opening.
  g.poly(chamfered(0, 0, w, h, 3)).fill({ color: 0x0d0904 });
  g.poly(chamfered(1, 1, w - 2, h - 2, 3)).fill({ color: 0x2b1a10 });
  // The bead: four rows of gilt relief with a light direction, not a flat rule.
  // Dark keyline outermost, the metal, its lit top face, and the shadow the
  // metal throws on the reading surface below it. Same ordering as every other
  // opening in this chrome, which is what makes the strip read as part of the
  // bar rather than as a control dropped into it.
  g.poly(chamfered(2, 2, w - 4, h - 4, 3)).fill({ color: 0x1d1408 });
  g.poly(chamfered(3, 3, w - 6, h - 6, 2)).fill({ color: COLOR.giltDark });
  g.poly(chamfered(4, 4, w - 8, h - 8, 2)).fill({ color: 0x6b5111 });
  g.rect(3, 3, w - 6, 1).fill({ color: COLOR.gilt, alpha: 0.85 });
  g.rect(3, h - 4, w - 6, 1).fill({ color: 0x120c05, alpha: 0.95 });
  // A dark red rule inside the bead, and then a near-black reading surface.
  //
  // The classic's message panel is not a brown recess with a gold bead: sampled on
  // a reference screenshot, its interior is (58,37,20) - about a third
  // of the value of the console's own leather - and the frame inside its bevel
  // runs (124,22,35) to (133,13,23), a dark crimson rule. Ours was a lit brown
  // plank at 0x33260f inside four rows of gilt, which is the brightest surface
  // on the console rather than the quietest.
  g.poly(chamfered(5, 5, w - 10, h - 10, 2)).fill({ color: MESSAGE_RULE });
  g.poly(chamfered(6, 6, w - 12, h - 12, 1)).fill({ color: MESSAGE_FIELD });
  grain(g, 6, 6, w - 12, h - 12, 67, 0.55);
  woodGrain(g, 6, 6, w - 12, h - 12, 23);
  for (let i = 0; i < 5; i++) {
    g.rect(6, 6 + i, w - 12, 1).fill({ color: 0x000000, alpha: 0.26 * (1 - i / 5) });
    g.rect(6, h - 7 - i, w - 12, 1).fill({ color: 0x000000, alpha: 0.14 * (1 - i / 5) });
  }
  g.rect(6, 6, 1, h - 12).fill({ color: 0x000000, alpha: 0.35 });
  return g;
}

/** A gilt-framed sub-plate for a block of console content. */
export function paintPlaque(g: Graphics, w: number, h: number, seed: number): Graphics {
  g.clear();
  g.rect(0, 0, w, h).fill({ color: 0x120c07 });
  g.rect(1, 1, w - 2, h - 2).fill({ color: COLOR.giltDark, alpha: 0.9 });
  g.rect(2, 2, w - 4, h - 4).fill({ color: 0x332514 });
  grain(g, 2, 2, w - 4, h - 4, seed, 0.8);
  g.rect(2, 2, w - 4, 1).fill({ color: COLOR.woodLit, alpha: 0.4 });
  g.rect(2, h - 3, w - 4, 1).fill({ color: 0x000000, alpha: 0.6 });
  cutCorners(g, w, h, CHAMFER, 0x120c07);
  return g;
}

/**
 * The socket a hero's painted portrait is set into.
 *
 * A face dropped straight onto the plaque reads as a sticker; the classic sets its
 * portraits behind a bevelled opening cut through the console's own frame, so
 * this is that opening: a chamfered gilt rim, a dark cast shadow along the two
 * upper edges of the aperture, and a warm stone floor for the picture to sit on
 * rather than a black hole. The picture itself is a Sprite the caller masks to
 * this box; the last two edges are painted over it by `paintPortraitLip`.
 */
export function paintPortraitSocket(
  g: Graphics, x: number, y: number, w: number, h: number, tint: number,
): void {
  g.poly(chamfered(x - 1, y - 1, w + 2, h + 2, CHAMFER)).fill({ color: 0x0a0704 });
  g.poly(chamfered(x, y, w, h, CHAMFER - 1)).fill({ color: COLOR.giltDark });
  g.poly(chamfered(x + 1, y + 1, w - 2, h - 2, CHAMFER - 1)).fill({ color: COLOR.gilt, alpha: 0.55 });
  g.poly(chamfered(x + 2, y + 2, w - 4, h - 4, CHAMFER - 2)).fill({ color: 0x100c07 });
  // A warm floor, lit from above: the picture is lit against a wall, not a void.
  for (let i = 0; i < 6; i++) {
    const k = i / 6;
    g.rect(x + 2, y + 2 + (h - 4) * k, w - 4, (h - 4) / 6 + 0.5)
      .fill({ color: mixColor(0x3a2c1b, tint, 0.22), alpha: 0.5 * (1 - k) + 0.08 });
  }
}

/** The lip of a portrait socket, painted over the picture so it sits *in* the hole. */
export function paintPortraitLip(g: Graphics, x: number, y: number, w: number, h: number): void {
  g.rect(x + 2, y + 2, w - 4, 1).fill({ color: 0x000000, alpha: 0.75 });
  g.rect(x + 2, y + 3, w - 4, 1).fill({ color: 0x000000, alpha: 0.35 });
  g.rect(x + 2, y + 2, 1, h - 4).fill({ color: 0x000000, alpha: 0.6 });
  g.rect(x + 2, y + h - 3, w - 4, 1).fill({ color: COLOR.giltLit, alpha: 0.3 });
  g.rect(x + w - 3, y + 2, 1, h - 4).fill({ color: COLOR.giltLit, alpha: 0.22 });
  g.poly(chamfered(x, y, w, h, CHAMFER - 1)).stroke({ width: 1, color: 0x140f08, alpha: 0.9, alignment: 0 });
}

/**
 * A small socket for one icon: the four primaries in a hero plaque sit in these.
 *
 * The socket is the alcove the device sits in: a cut edge, a gilt bevel and a
 * warm floor with a shadow along its top lip, so the icon reads as struck into
 * the plaque rather than printed on it.
 *
 * The floor is load-bearing now, not decorative. The icon sheet carries real
 * alpha (`ui.primary.attack`: 6211 of 9216 texels fully clear, 0 partial across
 * all 293 icon frames), so a bare Sprite leaves everything outside the device
 * showing whatever is underneath - and what is underneath has to be a lit floor
 * or the device floats on the plaque.
 *
 * An earlier note here declared the sheet alpha-less and told the caller to draw
 * these sprites in `screen`. That was true of a broken pack and is false now -
 * the packer's `alpha: "opaque"` mode had been flattening these frames to black.
 * Both the note and the blend are gone; see `packages/render/test/icon-alpha.spec.ts`.
 */
export function paintIconSocket(
  g: Graphics, x: number, y: number, s: number, floor = 0x2a2015,
): void {
  g.poly(chamfered(x, y, s, s, 2)).fill({ color: 0x0b0805 });
  g.poly(chamfered(x + 1, y + 1, s - 2, s - 2, 2)).fill({ color: COLOR.giltDark, alpha: 0.85 });
  // The floor is a parameter because four gilt devices on four identical brown
  // floors are four gold smudges at 21 device pixels: the icon sheet's own
  // silhouettes (a sword, a shield, an orb, a book) do not separate at that
  // size, and measured on the shipped shot the four sockets differed by under
  // 3 luma. Giving each primary its own floor sorts them by colour, which is
  // the one channel still working at this size - and it is what the hero screen
  // already does with the same four devices.
  g.poly(chamfered(x + 2, y + 2, s - 4, s - 4, 1)).fill({ color: floor });
  g.rect(x + 2, y + 2, s - 4, 1).fill({ color: 0x000000, alpha: 0.6 });
  g.rect(x + 2, y + s - 3, s - 4, 1).fill({ color: COLOR.stoneLit, alpha: 0.22 });
}

/**
 * The floor colour behind each of the four primaries.
 *
 * Attack is the warm iron of a blade struck on an anvil, defence the cold steel
 * of a shield, spell power the violet of a charged orb and knowledge the deep
 * blue of an open book - the four hues the classic's own primary icons are painted in,
 * used here as the socket's floor rather than as a tint on the device, so the
 * gilt reads as gilt on all four.
 */
export const PRIMARY_FLOOR: Readonly<Record<string, number>> = {
  "ui.primary.attack": 0x4a1f14,
  "ui.primary.defense": 0x2c3844,
  "ui.primary.spell_power": 0x3a2450,
  "ui.primary.knowledge": 0x1d3550,
};

/**
 * A reading meter: mana here, and anything else with a fill and a maximum.
 *
 * The bar this replaces was three flat rectangles - a 0x0d0a06 track, a 0x2f56ad
 * fill and a 3px lighter cap - so the fill was fifteen rows of one colour with a
 * hard edge and no material. A meter reads as glass when it has a ramp across
 * its height, a bright meniscus at the head of the fill, and ticks cut into the
 * track behind it so the eye can see how full it is without reading the number.
 */
export function paintMeter(
  g: Graphics, x: number, y: number, w: number, h: number, frac: number, hue: number,
): void {
  const f = Math.max(0, Math.min(1, frac));
  g.poly(chamfered(x, y, w, h, 2)).fill({ color: 0x0a0704 });
  g.poly(chamfered(x + 1, y + 1, w - 2, h - 2, 1)).fill({ color: 0x1a140c });
  // Ticks in the empty track: quarters, so the trough is not one flat colour.
  for (let i = 1; i < 4; i++) {
    g.rect(Math.round(x + (w * i) / 4), y + 2, 1, h - 4).fill({ color: 0x000000, alpha: 0.55 });
    g.rect(Math.round(x + (w * i) / 4) + 1, y + 2, 1, h - 4).fill({ color: COLOR.stoneLit, alpha: 0.12 });
  }
  const fw = Math.max(0, (w - 2) * f);
  if (fw >= 1) {
    // Five bands across the height: dark at the bottom, lit just under the top,
    // which is what a cylinder of liquid does under a single light.
    for (let i = 0; i < 5; i++) {
      const k = i / 5;
      const c = k < 0.45 ? mixColor(hue, 0xffffff, 0.34 - k * 0.5) : mixColor(hue, 0x05070f, k * 0.55);
      g.rect(x + 1, y + 1 + (h - 2) * k, fw, (h - 2) / 5 + 0.6).fill({ color: c });
    }
    // The meniscus: the head of the fill catches the light along its whole edge.
    g.rect(x + 1 + fw - 1.4, y + 1, 1.4, h - 2).fill({ color: mixColor(hue, 0xffffff, 0.6), alpha: 0.9 });
    g.rect(x + 1, y + 1, fw, 1).fill({ color: mixColor(hue, 0xffffff, 0.55), alpha: 0.7 });
    g.rect(x + 1, y + h - 2, fw, 1).fill({ color: 0x000000, alpha: 0.4 });
  }
  g.poly(chamfered(x, y, w, h, 2)).stroke({ width: 1, color: COLOR.giltDark, alpha: 0.9, alignment: 0 });
}

export type ButtonMood = "idle" | "hover" | "disabled";

/**
 * A carved stone button with a gilt bezel, lit from the upper left. Pressed and
 * disabled are the same block with the light source moved or removed, not a
 * different colour: a button that changes hue on hover reads as a web control.
 *
 * The face is quarried stone - a value ramp, speckle, and two chipped corners -
 * because a flat mid-grey rectangle with a 1px stroke was the one thing on this
 * screen three reviewers each called a placeholder.
 */
export function paintStoneButton(g: Graphics, w: number, h: number, mood: ButtonMood, seed = 5): Graphics {
  g.clear();
  const dim = mood === "disabled";
  const lit = mood === "hover";
  g.rect(2, 4, w, h).fill({ color: COLOR.shadow, alpha: dim ? 0.3 : 0.6 });
  // Bezel: dark keyline, gilt body, dark inner keyline. Three tones, not one stroke.
  g.rect(0, 0, w, h).fill({ color: 0x0d0904 });
  g.rect(1, 1, w - 2, h - 2).fill({ color: dim ? COLOR.giltDark : COLOR.gilt });
  g.rect(1, 1, w - 2, 1).fill({ color: dim ? COLOR.gilt : COLOR.giltLit, alpha: 0.9 });
  g.rect(1, h - 2, w - 2, 1).fill({ color: 0x000000, alpha: 0.5 });
  g.rect(3, 3, w - 6, h - 6).fill({ color: 0x0d0904 });
  // Stone face. Warm, and two values across the block rather than one: the whole
  // face used to be a single mid-grey, which is what "flat grey plate" means.
  // Warm, not grey. The reference's console buttons are dark brown - sampled
  // around (90,60,35) between the icons - and a genuinely grey plate carrying a
  // gold icon inside a brown-and-gilt bar reads as a control borrowed from
  // another program. The stone is mixed most of the way to wood.
  const face = mixColor(COLOR.stone, COLOR.wood, 0.55);
  const base = dim
    ? mixColor(COLOR.stoneDark, COLOR.woodDark, 0.55)
    : lit ? mixColor(face, COLOR.stoneLit, 0.28) : face;
  g.rect(4, 4, w - 8, h - 8).fill({ color: base });
  // The block is cut from quarried rock: broad diagonal value bands, then a
  // chisel-mark pass, then speckle. Three passes is what stops it reading flat.
  for (let i = 0; i < 7; i++) {
    const t = i / 7;
    const v = noise(seed, i * 5 + 1);
    g.poly([
      4 + (w - 8) * t, 4,
      4 + (w - 8) * (t + 0.16), 4,
      4 + (w - 8) * (t + 0.16) - 7, h - 4,
      4 + (w - 8) * t - 7, h - 4,
    ]).fill({ color: v > 0.5 ? COLOR.stoneLit : COLOR.stoneShadow, alpha: (0.05 + v * 0.07) * (dim ? 0.5 : 1) });
  }
  for (let i = 0; i < 16; i++) {
    const cx = 5 + noise(seed, i * 7 + 3) * (w - 10);
    const cy = 5 + noise(seed, i * 7 + 4) * (h - 10);
    const len = 2 + noise(seed, i * 7 + 5) * 5;
    g.rect(cx, cy, len, 1).fill({ color: COLOR.stoneShadow, alpha: dim ? 0.12 : 0.3 });
    g.rect(cx, cy + 1, len, 1).fill({ color: COLOR.stoneLit, alpha: dim ? 0.08 : 0.22 });
  }
  grain(g, 4, 4, w - 8, h - 8, seed, dim ? 0.5 : 1.05);
  // A chipped corner apiece, so no two buttons are the same block.
  g.poly([4, 4, 4 + 5 + noise(seed, 1) * 4, 4, 4, 4 + 4 + noise(seed, 2) * 4])
    .fill({ color: COLOR.stoneLit, alpha: dim ? 0.1 : 0.3 });
  g.poly([w - 4, h - 4, w - 4 - 6 - noise(seed, 3) * 4, h - 4, w - 4, h - 4 - 5 - noise(seed, 4) * 4])
    .fill({ color: COLOR.stoneShadow, alpha: 0.45 });
  // The recess the block sits in: the bezel casts a shadow across the top-left
  // of the face and the face catches light on its lower-right lip. Without the
  // cast shadow the block is level with its frame and nothing reads as inset.
  // The bezel throws a shadow across the top-left of the face and the face
  // catches light along its bottom-right: a soft two-step each way, because a
  // hard 2px band either side reads as a second frame inside the first.
  for (let i = 0; i < 4; i++) {
    const a = (dim ? 0.1 : 0.2) * (1 - i / 4);
    g.rect(4, 4 + i, w - 8, 1).fill({ color: 0x0b0803, alpha: a });
    g.rect(4 + i, 4, 1, h - 8).fill({ color: 0x0b0803, alpha: a * 0.8 });
    g.rect(4, h - 5 - i, w - 8, 1).fill({ color: COLOR.stoneLit, alpha: (lit ? 0.34 : 0.2) * (1 - i / 4) });
    g.rect(w - 5 - i, 4, 1, h - 8).fill({ color: COLOR.stoneLit, alpha: (lit ? 0.26 : 0.16) * (1 - i / 4) });
  }
  if (lit) g.rect(4, 4, w - 8, h - 8).fill({ color: COLOR.giltLit, alpha: 0.08 });
  cutCorners(g, w, h, CHAMFER, 0x0d0904);
  return g;
}

/**
 * Lay a glyph on a console button as **gilt relief**.
 *
 * It used to be a groove cut into stone, and that was the wrong material. Every
 * icon on the classic's own console is bronze-gold line work standing proud of a warm
 * brown face - the crossed swords, the running man, the hourglass, the open book
 * - not a dark channel cut into grey. That much holds; the numbers that used to
 * be attached to it did not.
 *
 * The old citation was a reference screenshot,
 * which **is not on disk**, for glyph pixels "around (200,170,90)" on a face of
 * "(90,60,35)". Measured on 2026-08-30 on three frames that do exist - eleven
 * button interiors in all, taking the brightest 12% of each interior as the lit
 * metal and its middle 30% as the face:
 *
 *   a reference screenshot, four left buttons    glyph (199,186,129)  face (70,46,22)
 *   a reference screenshot, three right buttons  glyph (219,207,147)  face (71,48,24)
 *   CLASSIC_Battlefield.jpg, four         glyph (201,182,124)  face (68,44,21)
 *   a reference screenshot, four                 glyph (199,184,138)  face (66,41,21)
 *
 * So the metal is far **paler** than the citation said (blue 124-147, not 90)
 * and the face **darker** (luma 45-51, not 64): the real contrast is 3.8x, not
 * 2.4x. Our own `COLOR.gilt` is (201,162,39) - the right red, a much lower blue
 * - and it is deliberately left alone here, because it frames every plate on
 * this screen and re-grading it off a top-decile sample of four JPEGs would move
 * the whole console to fix an icon. Recorded as a known divergence, not fixed.
 *
 * **Five passes, and the middle three are the change.** They used to be five
 * traces of an outline at five offsets, which is a line drawing however it is
 * shaded: measured on the shipped console, all eight buttons were a single-value
 * gold contour at one stroke width with no fill, no second tone and no shadow,
 * sitting under painted hero portraits and 110px painted creature sprites.
 * The classic's own console buttons are small painted objects - `rig/ref/battle.png`,
 * buttons one to four, are a book, a flag, a running man and crossed swords,
 * each a filled pale-gold body with a darker shade in it and a lit rim on top -
 * so the body pass fills the glyph's masses and only the two rim passes trace
 * them.
 *
 * The order is the order light falls: the shadow the object throws on the face,
 * the object, the shaded edge under it, the lit edge over it.
 */
/**
 * The two tones a resting console glyph is struck in: the leather's own value a
 * stop up from the plate, and the light on its upper rim. Measured off the
 * reference's own buttons, whose devices sample around (110,80,50) on a face
 * around (90,60,35).
 */
const GLYPH_LEATHER = 0x6e5233;
const GLYPH_LEATHER_LIT = 0xc0a173;

export function engrave(
  g: Graphics,
  draw: (g: Graphics, colour: number, alpha: number, dy: number, pass: GlyphPass) => void,
  mood: ButtonMood,
): void {
  const dim = mood === "disabled";
  const lit = mood === "hover";
  const body: GlyphPass = { solid: true, weight: 1 };
  // Half weight on the rims: at full weight the two of them, offset 1.8 apart,
  // met in the middle and the body under them stopped being visible at all.
  const rim: GlyphPass = { solid: false, weight: 0.5 };
  // **Gold is the pointer's, not the button's.** Every glyph on this strip used
  // to be laid in `COLOR.gilt` whatever its mood, and a critic read the result
  // as "flat single-colour gold line-art glyphs on grey plates". The classic's are
  // the opposite: cropped off a reference screenshot at x240..640, the
  // four console buttons carry BROWN devices on brown leather - a cog pair, a
  // flag, a running figure - shaded rather than coloured, and only the button
  // under the cursor (its crossed swords) is gold on a gilt-framed plate. So
  // the resting glyph is the leather it is struck into, lit from above, and the
  // hover state keeps the gold.
  const gold = lit;
  draw(g, 0x0a0602, dim ? 0.3 : 0.55, 2.6, body);
  draw(g, 0x180f05, dim ? 0.3 : 0.7, 1.3, body);
  draw(g, dim ? 0x3a2c19 : gold ? COLOR.gilt : GLYPH_LEATHER, 1, 0, body);
  draw(g, dim ? 0x2a1f12 : gold ? 0x7a5c14 : 0x241a0d, dim ? 0.5 : 0.9, 0.9, rim);
  draw(
    g,
    dim ? 0x6b5312 : gold ? 0xfff3cf : GLYPH_LEATHER_LIT,
    dim ? 0.45 : 0.95, -0.9, rim,
  );
}

/**
 * The eight command glyphs.
 *
 * They are **silhouettes with an outline on them**, not line work. The note this
 * replaces argued the other way - "a solid black shape on a stone face is a
 * printed stencil however it is shaded" - and it was answering the wrong
 * failure. A silhouette in the *face's own dark* is a stencil; a silhouette in
 * pale gold with a shaded rim under it and a lit rim over it is a casting, which
 * is what every icon on the classic's own console is. What the outline-only set
 * actually cost is legibility at size: two of the eight (the boot and the sword)
 * could not be identified at all by a reader who did not already know what they
 * were, because an outline at 3.3 device pixels on a 36-pixel glyph spends a
 * tenth of the drawing's height on its own edge and leaves nothing inside it.
 *
 * Every drawing is therefore laid down as *masses* - closed contours that are
 * filled on the body pass and traced on the two rim passes - plus *seams*, which
 * are the lines cut into a mass and are drawn darker than it. `engrave` decides
 * which pass is which; a drawing never knows.
 */

/**
 * A pass of `engrave`: whether the drawing's masses are filled or traced, and
 * the stroke weight a trace uses.
 *
 * It is a parameter rather than module state because `measureGlyph` runs every
 * drawing at import to find its own box, and a drawing whose geometry depended
 * on a mutable flag could be measured in one shape and painted in another.
 */
export interface GlyphPass {
  readonly solid: boolean;
  readonly weight: number;
}

/** What a drawing gets when nothing says otherwise: one trace at full weight. */
const LINE_PASS: GlyphPass = { solid: false, weight: 1 };

/**
 * Line weight of every console glyph, in arena pixels.
 *
 * It used to be `max(1.6, s * 0.13)` - a fraction of the size the glyph was
 * asked to draw at - and that reads as one icon set only while every glyph is
 * the same size. These are not: the console normalises each drawing onto a
 * common box (`GLYPH_ART`), so a busy glyph is laid down at two thirds the scale
 * of a plain one, and a proportional stroke would have come out two thirds as
 * heavy beside it. Across the eight drawings here the scales differ by 1.9x,
 * which is 1.9x of line weight on one strip of eight buttons.
 *
 * Weight is the quantity that is constant across a set of icons; scale is the
 * one that varies. 2.2 arena pixels is 3.3 device pixels at 1920x1080, which is
 * what the old rule produced at the median glyph size.
 */
const GLYPH_STROKE = 2.2;

type CutStyle = { width: number; color: number; alpha: number; cap: "round"; join: "round" };

/**
 * The ink one drawing uses for one pass: a mass, a round mass, and a seam.
 *
 * `mass` and `blob` are the same shape in both modes - that is what lets
 * `measureGlyph` measure a drawing once and have the number hold for every pass.
 * A seam is a cut into a mass, so on the body pass it is drawn in a darkened
 * body colour: on a filled shape a seam in the body's own colour is invisible,
 * which is how a shield's rib and a crown's moulding used to disappear the
 * moment anything was filled.
 */
interface GlyphInk {
  readonly mass: (g: Graphics, pts: readonly number[]) => void;
  readonly blob: (g: Graphics, x: number, y: number, r: number) => void;
  readonly seam: (g: Graphics, pts: readonly number[], weight?: number) => void;
  /** Finish a path the drawing has already laid down with the fluent API. */
  readonly close: (g: Graphics) => void;
}

function glyphInk(c: number, a: number, p: GlyphPass): GlyphInk {
  const st: CutStyle =
    { width: GLYPH_STROKE * p.weight, color: c, alpha: a, cap: "round", join: "round" };
  const finish = (g: Graphics): void => {
    if (p.solid) g.fill({ color: c, alpha: a });
    else g.stroke(st);
  };
  const seamStyle = (weight: number): CutStyle => ({
    ...st,
    width: GLYPH_STROKE * weight * (p.solid ? 1 : p.weight),
    color: p.solid ? mixColor(c, 0x0a0602, 0.62) : c,
  });
  return {
    mass: (g, pts) => { g.poly([...pts], true); finish(g); },
    blob: (g, x, y, r) => { g.circle(x, y, r); finish(g); },
    seam: (g, pts, weight = 0.5) => {
      g.moveTo(pts[0]!, pts[1]!);
      for (let i = 2; i + 1 < pts.length; i += 2) g.lineTo(pts[i]!, pts[i + 1]!);
      g.stroke(seamStyle(weight));
    },
    close: finish,
  };
}

/**
 * A limb, a pole, a shaft: a segment with a width, as a closed quad.
 *
 * Drawn as a mass rather than as a thick stroke because a stroke's width is
 * `engrave`'s to vary and a limb's is not - a running figure whose legs thinned
 * to a hairline on the rim passes is a stick figure again on two passes out of
 * five, and a stick figure is the drawing that failed here twice.
 */
function limb(
  x0: number, y0: number, x1: number, y1: number, w: number,
): readonly number[] {
  const dx = x1 - x0, dy = y1 - y0;
  const len = Math.max(1e-6, Math.hypot(dx, dy));
  const nx = (-dy / len) * (w / 2), ny = (dx / len) * (w / 2);
  return [x0 + nx, y0 + ny, x1 + nx, y1 + ny, x1 - nx, y1 - ny, x0 - nx, y0 - ny];
}

/** An hourglass with its sand run through: wait. */
export function glyphWait(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const at = (x: number, y: number): readonly number[] => [s * x, s * y + dy];
  const seg = (
    x0: number, y0: number, x1: number, y1: number, w: number,
  ): readonly number[] => limb(s * x0, s * y0 + dy, s * x1, s * y1 + dy, s * w);
  ink.mass(g, seg(-0.50, -0.66, 0.50, -0.66, 0.15));
  ink.mass(g, seg(-0.50, 0.66, 0.50, 0.66, 0.15));
  // The glass: two bowls meeting at a waist, one closed contour. The waist is
  // 0.10 across rather than a point, because a fill that pinches to zero width
  // breaks into two triangles with a gap between them at any size.
  ink.mass(g, [
    ...at(-0.34, -0.52), ...at(0.34, -0.52), ...at(0.05, 0),
    ...at(0.34, 0.52), ...at(-0.34, 0.52), ...at(-0.05, 0),
  ]);
  // The sand still to fall, cut into the upper bowl.
  ink.seam(g, [...at(-0.19, -0.28), ...at(0.19, -0.28)], 0.5);
}

/** A kite shield with a rib and a boss: defend. */
export function glyphDefend(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const w = s * 0.5, h = s * 0.62;
  g.moveTo(-w, -h + dy)
    .lineTo(w, -h + dy)
    .lineTo(w * 0.86, h * 0.16 + dy)
    .quadraticCurveTo(w * 0.5, h * 0.86 + dy, 0, h + dy)
    .quadraticCurveTo(-w * 0.5, h * 0.86 + dy, -w * 0.86, h * 0.16 + dy)
    .closePath();
  ink.close(g);
  // A cross, not a rib and a boss: on a filled shield a boss is a dot and a rib
  // is a scratch, and the pair of them read as a dagger laid on the face - which
  // is the one thing this glyph must not read as, with the hero's sword three
  // buttons away. A cross is heraldry, it is the charge our own knight carries
  // on the field, and its arms are long enough to survive the rim passes.
  ink.seam(g, [0, -h * 0.66 + dy, 0, h * 0.62 + dy], 0.55);
  ink.seam(g, [-w * 0.56, -h * 0.2 + dy, w * 0.56, -h * 0.2 + dy], 0.55);
}

/**
 * A man running, out of the frame to the left: retreat.
 *
 * Four drawings have failed here, and the fourth is the one that says what the
 * rule is. A stick figure beside a bar read as a pedestrian crossing; a stick
 * figure under an arch read as two unrelated strokes; a boot **outlined** read
 * as a sock or a numeral 7, and it read that way for the same reason as the
 * first two - at the size this button paints, an outline is mostly edge, and a
 * shape a reader has to fill in mentally gets filled in with whatever is
 * nearest.
 *
 * The classic puts a running man on this button (`rig/ref/battle.png`, third from the
 * left) and it is legible at about thirty device pixels, which is smaller than
 * ours. It is legible because it is a **filled** figure: the stride reads as a
 * stride because the legs are masses with air between them, not lines. So this
 * is a figure again, drawn the way that one is - head, a leaning torso, two arms
 * and two legs, every one of them a mass - and it runs to the left, which is the
 * direction this console already treats as away.
 */
export function glyphRetreat(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const seg = (
    x0: number, y0: number, x1: number, y1: number, w: number,
  ): readonly number[] => limb(s * x0, s * y0 + dy, s * x1, s * y1 + dy, s * w);
  const at = (x: number, y: number): readonly number[] => [s * x, s * y + dy];
  // Trailing limbs first, so the near arm and the near leg overlap the torso.
  // Both arms are bent at the elbow - two bars each rather than one, because a
  // straight bar out of each shoulder is a gingerbread man - and they swing
  // opposite ways, the leading one up in front and the trailing one down
  // behind. Bent the same way they read as a figure with its hands up.
  ink.mass(g, seg(0.02, -0.24, 0.30, -0.06, 0.13));
  ink.mass(g, seg(0.30, -0.06, 0.40, 0.16, 0.12));
  ink.mass(g, seg(0.06, 0.12, 0.34, 0.30, 0.16));
  ink.mass(g, seg(0.34, 0.30, 0.60, 0.14, 0.14));
  ink.mass(g, [
    ...at(-0.32, -0.38), ...at(0.00, -0.32), ...at(0.16, 0.14), ...at(-0.16, 0.18),
  ]);
  // The head sits *into* the shoulders: at a gap of even 0.04 it reads as a ball
  // beside a figure rather than as the figure's own head.
  ink.blob(g, -s * 0.20, -s * 0.50 + dy, s * 0.17);
  ink.mass(g, seg(-0.16, -0.28, -0.44, -0.18, 0.13));
  ink.mass(g, seg(-0.44, -0.18, -0.52, -0.40, 0.12));
  ink.mass(g, seg(-0.06, 0.10, -0.42, 0.28, 0.16));
  ink.mass(g, seg(-0.42, 0.28, -0.40, 0.62, 0.14));
  ink.mass(g, seg(-0.40, 0.62, -0.62, 0.66, 0.12));
}

/** A standard lowered over a sword laid on the ground: surrender. */
export function glyphSurrender(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const at = (x: number, y: number): readonly number[] => [s * x, s * y + dy];
  const seg = (
    x0: number, y0: number, x1: number, y1: number, w: number,
  ): readonly number[] => limb(s * x0, s * y0 + dy, s * x1, s * y1 + dy, s * w);
  // The pole, leaning: a standard being lowered, not an upright banner.
  ink.mass(g, seg(-0.56, 0.50, 0.06, -0.74, 0.13));
  // The cloth, hanging from the raised end with a swallow tail.
  ink.mass(g, [
    ...at(0.04, -0.68), ...at(0.72, -0.40), ...at(0.40, -0.16),
    ...at(0.66, 0.08), ...at(-0.16, -0.20),
  ]);
  // A blade laid down beneath it, hilt to the left: the sword is given up.
  ink.mass(g, seg(-0.76, 0.74, 0.52, 0.74, 0.14));
  ink.mass(g, seg(-0.50, 0.54, -0.50, 0.94, 0.12));
  ink.blob(g, -s * 0.78, s * 0.74 + dy, s * 0.11);
}

/**
 * A sword, whole, with one spark off its point: the hero's own blow.
 *
 * Three drawings have failed here. A gauntleted fist gripping a short blade read
 * as a rake; a blade with three rays read as a key, because it was five strokes
 * that never touched and the eye assembles marks that do not join into whatever
 * it already knows; and the single closed contour that replaced it read as "a
 * thin diagonal stick with a blob spark", because an outlined blade 0.42 across
 * is two lines 1.5 device pixels apart and the fill between them was never
 * there.
 *
 * It is one mass now, 0.52 across, and the spark is a four-pointed star mass set
 * clear of the point. It is drawn on the diagonal, and that is a size decision
 * rather than a stylistic one: `glyphPlacement` normalises each drawing's
 * longest side onto `GLYPH_EXTENT`, so an upright sword - 1.5 long by 0.5 across
 * - is laid down at a third of the scale of a square drawing and comes out a
 * thin tall scratch in a square hole. On the diagonal the blade's own length is
 * the box's diagonal, so the same extent buys a blade half again as long.
 *
 * It has to read against `glyphSurrender` two buttons away, which also carries a
 * blade: that one lies flat on the ground under a lowered banner, this one is on
 * the diagonal, whole, and throwing a spark.
 */
export function glyphHeroStrike(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  // Blade-local: `u` runs from the grip to the point, `v` across the blade.
  const k = 0.7071;
  const at = (u: number, v: number): readonly number[] =>
    [s * (u - v) * k, s * (u + v) * k + dy];
  // No pommel, and that is the whole of what made an earlier one a key: a key is
  // a shaft with a bow at one end and teeth at the other, and a fat block behind
  // the guard with a notch at the tip is that silhouette exactly. What is left
  // is the three parts a sword cannot be read without - a long tapered blade, a
  // straight guard across it, and a grip - in one closed contour.
  const sword: readonly (readonly [number, number])[] = [
    [1.26, 0], [1.00, 0.26], [0.10, 0.27], [0.10, 0.46], [-0.18, 0.46],
    [-0.18, 0.16], [-0.66, 0.16], [-0.66, -0.16], [-0.18, -0.16], [-0.18, -0.46],
    [0.10, -0.46], [0.10, -0.27], [1.00, -0.26],
  ];
  const path: number[] = [];
  for (const [u, v] of sword) path.push(...at(u, v));
  ink.mass(g, path);
  // The blow: one four-pointed star off the point. It stands 0.4 clear of the
  // tip because a spark set closer is a notch on the end of the blade, which is
  // a key's teeth and not a flash.
  const spark: number[] = [];
  for (const [u, v] of [
    [2.12, 0], [1.90, 0.09], [1.83, 0.31], [1.76, 0.09], [1.54, 0],
    [1.76, -0.09], [1.83, -0.31], [1.90, -0.09],
  ] as const) {
    spark.push(...at(u, v));
  }
  ink.mass(g, spark);
}

/**
 * A crown: the army's own charge, spent once in a battle.
 *
 * It was a horned sigil inside a three-quarter ring, and the ring's gap was
 * carrying the whole meaning - "a seal being spent rather than an action
 * repeated". A 1.7-radian gap at the top of a circle this size is not a readable
 * difference from a closed one; what the button read as was an omega with a
 * trident in it, two buttons from the spellbook's closed star.
 *
 * A crown says the same thing without depending on a gap: it is the faction's
 * own device rather than a rune, it is the only glyph on the strip built out of
 * horizontals, and nothing else here has three points on a band.
 */
export function glyphRacial(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const w = s * 0.72;
  const base = s * 0.44;
  const at = (x: number, y: number): readonly number[] => [x, y + dy];
  ink.mass(g, [
    ...at(-w, base), ...at(-w, -s * 0.16), ...at(-w * 0.46, s * 0.06),
    ...at(0, -s * 0.66), ...at(w * 0.46, s * 0.06), ...at(w, -s * 0.16),
    ...at(w, base),
  ]);
  // The band's own moulding, and a jewel on each point: what keeps it from
  // reading as a plain zigzag on a bar.
  ink.seam(g, [-w, base - s * 0.2 + dy, w, base - s * 0.2 + dy], 0.7);
  ink.seam(g, [-s * 0.09, -s * 0.5 + dy, s * 0.09, -s * 0.5 + dy], 0.7);
}

/**
 * A bolt: a creature's activated ability. See the note inside on the brackets
 * this used to be drawn inside.
 */
export function glyphActivate(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const at = (x: number, y: number): readonly number[] => [s * x, s * y + dy];
  // **The brackets are gone.** They were meant to say "this belongs to the
  // stack, not to the hero"; what they actually said was punctuation. A critic
  // blind-read this button as a literal `[/]` and listed it as the one glyph on
  // the strip that could not be identified. A bolt on its own is a shape rather
  // than a character, and it is the only diagonal zigzag among eight glyphs
  // built from a crown, a book, an hourglass, a shield, a sword, a flag and a
  // running figure - so it needs no bracket to be told apart.
  ink.mass(g, [
    ...at(0.46, -0.80), ...at(0.02, -0.06), ...at(0.34, -0.06),
    ...at(-0.40, 0.80), ...at(-0.06, 0.10), ...at(-0.38, 0.10),
  ]);
}

/**
 * An open grimoire with a star rising off the page: the spellbook.
 *
 * The classic puts the book on the console as a button of its own, beside wait and
 * defend, and this screen had it as a line of text floating in the top-left
 * corner of the battlefield. The glyph has to read against an hourglass and a
 * shield, so it is two leaves meeting at a spine with the star of the mage
 * guild's own charge over them - the star is what separates it from a plain
 * book at a glance.
 */
export function glyphSpellbook(
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
): void {
  const ink = glyphInk(c, a, pass ?? LINE_PASS);
  const w = s * 0.54, h = s * 0.42;
  // Two leaves, each falling away from the spine, each a closed mass.
  for (const dir of [-1, 1] as const) {
    g.moveTo(0, -h * 0.66 + dy)
      .quadraticCurveTo(dir * w * 0.5, -h * 0.96 + dy, dir * w, -h * 0.62 + dy)
      .lineTo(dir * w, h * 0.62 + dy)
      .quadraticCurveTo(dir * w * 0.5, h * 0.3 + dy, 0, h * 0.58 + dy)
      .closePath();
    ink.close(g);
    // The page edges, cut into the leaf.
    ink.seam(g, [
      dir * w * 0.36, -h * 0.74 + dy, dir * w * 0.36, h * 0.2 + dy,
    ], 0.5);
  }
  // The spine.
  ink.seam(g, [0, -h * 0.66 + dy, 0, h * 0.58 + dy], 0.7);
  // The star over the open page: a six-pointed mass, not three crossed hairlines.
  const r = s * 0.26;
  const cy = -h * 1.02 - r * 0.5 + dy;
  const star: number[] = [];
  for (let i = 0; i < 12; i++) {
    const ang = (Math.PI / 6) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.42;
    star.push(Math.cos(ang) * rad, cy + Math.sin(ang) * rad);
  }
  ink.mass(g, star);
}

/**
 * Where each glyph's own drawing actually lands, measured off the drawing.
 *
 * Every one of the eight above was authored around its own origin at its own
 * extent, and nothing reconciled them: `glyphSurrender` spanned y -0.74s..+0.92s
 * (its sword lies on the button's bottom frame), `glyphRetreat` x -0.72s..+0.96s
 * (its arch runs into the right frame), and `glyphSpellbook` only 1.17s by
 * 0.97s - so drawn at one `s` the book came out 40% smaller than the shield
 * beside it and two of the eight touched their own bezel. None of them was
 * optically centred, because a shape whose ink runs from -0.74 to +0.92 is not
 * centred on 0.
 *
 * The fix is not to renumber eight drawings by hand - that is a table of
 * constants that goes stale the first time somebody edits a curve. It is to
 * measure each drawing, once, at import: run it against a sink that records
 * every point it lays down, and keep the box. The console then scales each
 * glyph so its box is the same box and offsets it by that box's own centre, so
 * a glyph edited later re-centres itself and no number here can drift from what
 * is actually drawn.
 *
 * The stroke is deliberately NOT included in the box. It is a constant
 * (`GLYPH_STROKE`) rather than a fraction of the size, so `glyphPlacement` can
 * subtract it once and hit an exact painted extent in one pass instead of
 * solving a fixed point.
 */
export type GlyphId =
  | "wait" | "defend" | "retreat" | "surrender"
  | "strike" | "racial" | "activate" | "spellbook";

export type GlyphDraw = (
  g: Graphics, s: number, c: number, a: number, dy: number, pass?: GlyphPass,
) => void;

export interface GlyphArt {
  readonly draw: GlyphDraw;
  /** The drawing's own path box at `s` = 1, `dy` = 0, stroke excluded. */
  readonly w: number;
  readonly h: number;
  /** The centre of that box: what a call site has to offset by to centre it. */
  readonly cx: number;
  readonly cy: number;
}

/**
 * A path sink that keeps the bounding box and draws nothing.
 *
 * It implements the part of the `Graphics` path API the glyphs use and no more.
 * A glyph that reaches for a method that is not here fails loudly at import,
 * which is the right failure: a silently unmeasured glyph would be laid out
 * against a box that does not contain it.
 */
function measureGlyph(draw: GlyphDraw): { w: number; h: number; cx: number; cy: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  let px = 0, py = 0;
  const at = (x: number, y: number): void => {
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  };
  const sink = {
    moveTo(x: number, y: number) { px = x; py = y; at(x, y); return sink; },
    lineTo(x: number, y: number) { px = x; py = y; at(x, y); return sink; },
    quadraticCurveTo(qx: number, qy: number, x: number, y: number) {
      // Sampled rather than taken from the control point: a control point is an
      // upper bound on the curve and would quietly shrink every glyph that has
      // one, which here is the shield, the book and the banner.
      const ax = px, ay = py;
      for (let i = 1; i <= 8; i++) {
        const t = i / 8, u = 1 - t;
        at(u * u * ax + 2 * u * t * qx + t * t * x, u * u * ay + 2 * u * t * qy + t * t * y);
      }
      px = x; py = y;
      return sink;
    },
    closePath() { return sink; },
    rect(x: number, y: number, w: number, h: number) { at(x, y); at(x + w, y + h); return sink; },
    circle(x: number, y: number, r: number) { at(x - r, y - r); at(x + r, y + r); return sink; },
    ellipse(x: number, y: number, rx: number, ry: number) {
      at(x - rx, y - ry); at(x + rx, y + ry); return sink;
    },
    poly(points: readonly number[]) {
      for (let i = 0; i + 1 < points.length; i += 2) at(points[i]!, points[i + 1]!);
      return sink;
    },
    fill() { return sink; },
    stroke() { return sink; },
  };
  draw(sink as unknown as Graphics, 1, 0xffffff, 1, 0);
  if (!Number.isFinite(x0)) return { w: 1, h: 1, cx: 0, cy: 0 };
  return { w: x1 - x0, h: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}

const glyphArt = (draw: GlyphDraw): GlyphArt => ({ draw, ...measureGlyph(draw) });

export const GLYPH_ART: Readonly<Record<GlyphId, GlyphArt>> = Object.freeze({
  wait: glyphArt(glyphWait),
  defend: glyphArt(glyphDefend),
  retreat: glyphArt(glyphRetreat),
  surrender: glyphArt(glyphSurrender),
  strike: glyphArt(glyphHeroStrike),
  racial: glyphArt(glyphRacial),
  activate: glyphArt(glyphActivate),
  spellbook: glyphArt(glyphSpellbook),
});

/**
 * The size and offset that put one glyph's painted ink in a box `extent` across,
 * centred on the button's own middle.
 *
 * The painted extent is the path's box times the scale, plus one stroke width,
 * so the scale is solved directly rather than iterated: `size = (extent -
 * GLYPH_STROKE) / max(w, h)`. A full stroke width is still the allowance even
 * though the masses are filled and only the rim passes trace them at half of
 * it: the rims are also offset by up to 0.9 either way and the cast shadow by
 * 2.6, so the composite still runs about a stroke wider than the box.
 */
export function glyphPlacement(
  art: GlyphArt, extent: number,
): { readonly size: number; readonly dx: number; readonly dy: number } {
  const span = Math.max(0.01, Math.max(art.w, art.h));
  const size = Math.max(0.1, (extent - GLYPH_STROKE) / span);
  return { size, dx: -art.cx * size, dy: -art.cy * size };
}

/**
 * The gilt bead down each edge of the side rail, in arena pixels.
 *
 * Exported because `ui.ts` lays the queue's plates inside it: the rail is
 * `RAIL_W` wide and only `RAIL_W - 2 * RAIL_LIP` of that is planking, so a
 * trough sized to the rail rather than to the planking hangs over the bead at
 * one end and over the window at the other. It did: the well was laid at
 * `railX + 5` with the rail's own width, which put 5 of its columns outside the
 * rail entirely.
 */
export const RAIL_LIP = 5;

/**
 * The frame's right edge: the turn order's vertical rail.
 *
 * The same carved oak and gilt as `paintCommandBand`, stood on end, so the rail
 * reads as a piece of the same frame the console is - a post standing at the
 * side of the board rather than a panel dropped on top of it. It is what
 * replaced the full-width band across the top of the frame; see
 * `initiativeLayout` for why.
 *
 * **Beaded on both edges, not one.** It used to carry the bead down the inner
 * edge alone, on the argument that the inner edge is the one the board sees.
 * What that cost is a joint: the horizontal cap ran the full width and stopped
 * flat at the outer edge, with nothing there for it to meet, so three corners
 * of the rail were mitred and the fourth was a butt with a dark notch in it -
 * which is exactly the reading "unfinished" that the mitre was introduced to
 * fix at the other three. A post has two edges.
 *
 * `y`/`h` are the rail's own extent, because it is cut to its contents: unlike
 * the band, which ran edge to edge, this one is only as long as the queue.
 */
export function paintSideRail(
  g: Graphics, x: number, y: number, w: number, h: number,
): Graphics {
  const lip = RAIL_LIP;
  // The rail's planking is lit two steps above the console's own
  // (`mixColor(wood, woodLit, 0.5)` rather than `wood`), and that is deliberate
  // rather than a drift. The console lies flat along the bottom of the frame and
  // is lit at a glancing angle; the rail is a post standing at the side of the
  // board, facing the light, and a post lit like a floor reads as a hole. It was
  // painted in the console's own tone and measured as the darkest thing on the
  // screen: rail mean luma 44 against a battlefield at 89, a 2.0x step DOWN,
  // where the expansion HD's sidebar sits at about 0.87x of its own field - very slightly
  // LIGHTER than the ground it stands beside, not half as bright.
  const plank = mixColor(COLOR.wood, COLOR.woodLit, 0.5);
  g.rect(x, y, w, h).fill({ color: COLOR.wood });
  g.rect(x + lip, y, w - 2 * lip, h).fill({ color: plank });
  woodGrain(g, x + lip, y, w - 2 * lip, h, 77);
  // Lit from the left, like everything else in this chrome, so the planking
  // darkens toward the window edge - but over a 0.03-0.08 range rather than
  // 0.03-0.14, because the far edge of a lit post is still lit.
  for (let i = 0; i < 9; i++) {
    const k = i / 9;
    g.rect(x + lip + (w - 2 * lip) * k, y, (w - 2 * lip) / 9, h)
      .fill({ color: 0x000000, alpha: 0.02 + k * 0.06 });
  }
  // A soft highlight down the inner third: the face that takes the light.
  for (let i = 0; i < 4; i++) {
    g.rect(x + lip + i * 3, y, 3, h)
      .fill({ color: COLOR.woodLit, alpha: 0.1 * (1 - i / 4) });
  }
  // The two gilt beads, then the shadow the inner one throws sideways onto the
  // battlefield: the rail is in front of the field.
  //
  // The bolts that used to run down the outer edge are gone. The queue's well
  // covers the whole planking between the two beads (`ui.ts` sizes it to
  // `RAIL_W - 2 * RAIL_LIP`), so every one of them was painted under it - dead
  // paint that only showed as a shape in the wood when the rail was short.
  for (const [bx, litFirst] of [[x, true], [x + w - lip, false]] as const) {
    g.rect(bx, y, lip, h).fill({ color: COLOR.giltDark });
    g.rect(bx + (litFirst ? 1 : lip - 3), y, 2, h).fill({ color: COLOR.gilt });
    g.rect(bx + (litFirst ? 1 : lip - 2), y, 1, h)
      .fill({ color: litFirst ? COLOR.giltLit : COLOR.giltDark });
  }
  g.rect(x - 1.6, y, 1.6, h).fill({ color: 0x000000, alpha: 0.55 });
  for (let i = 0; i < 10; i++) {
    g.rect(x - 1.6 - (i + 1) * 1.6, y, 1.6, h)
      .fill({ color: 0x000000, alpha: 0.08 * (1 - i / 10) });
  }
  // Capped at both ends rather than running off the frame, because it is cut to
  // the queue: a post has a top and a foot.
  //
  // **Mitred at all four corners.** The cap used to be `rect(x, cy, w, 4)`, a
  // full-width band laid across the top of a 5px vertical lip - so the two gilt
  // members met in a step: 4 rows of cap sitting on 5 columns of lip, with the
  // join drawn as a corner of one rectangle overlapping another. A picture frame
  // is mitred, and the reason this one has to be is that the lip and the cap are
  // the same metal at the same value, so a butt joint has nothing to read as
  // except a mistake. Cap depth is the bead's own width, which makes every mitre
  // an exact 45 degrees. Both ends of both caps, now that the rail is beaded on
  // both edges: with one bead, three corners were mitred and the fourth was the
  // butt this note was written about.
  for (const end of [0, 1] as const) {
    const cy = end === 0 ? y : y + h - lip;
    const outer = end === 0 ? cy : cy + lip;
    const inner = end === 0 ? cy + lip : cy;
    g.poly([x, outer, x + w, outer, x + w - lip, inner, x + lip, inner])
      .fill({ color: COLOR.giltDark });
    // The lit face of the cap: the top one takes the light, the foot is in its
    // own shadow, which is the same light source the planking is graded to.
    g.rect(x + lip, end === 0 ? cy + 1 : cy + lip - 2, w - 2 * lip, 1.5)
      .fill({ color: end === 0 ? COLOR.gilt : COLOR.giltDark });
    // The mitre lines themselves, so the joints are drawn rather than implied.
    const seam = { width: 1, color: 0x120c05, alpha: 0.55 };
    g.moveTo(x, outer).lineTo(x + lip, inner).stroke(seam);
    g.moveTo(x + w, outer).lineTo(x + w - lip, inner).stroke(seam);
  }
  return g;
}

/**
 * The frame's top edge: the docked initiative bar's band.
 *
 * Nothing draws this any more - the band it painted is gone, and the turn order
 * it carried runs down `paintSideRail` instead. It is kept because it is the
 * only description of the frame's top edge, and a design that wants one back
 * should start from it rather than from a fresh guess.
 */
export function paintTopBand(g: Graphics, x: number, w: number, h: number): Graphics {
  g.clear();
  g.rect(x, 0, w, h).fill({ color: COLOR.woodDark });
  g.rect(x, 0, w, h - 5).fill({ color: COLOR.wood });
  woodGrain(g, x, 0, w, h - 5, 77);
  for (let i = 0; i < 11; i++) {
    const k = i / 11;
    g.rect(x, (h - 5) * k, w, (h - 5) / 11)
      .fill({ color: 0x000000, alpha: 0.035 + Math.abs(k - 0.7) * 0.13 });
  }
  // Bolts along the top edge, mirroring the console's along its bottom.
  for (let bx = x + 16; bx < x + w - 10; bx += 58) {
    g.circle(bx, 10, 3.4).fill({ color: 0x0e0a06, alpha: 0.75 });
    g.circle(bx, 9, 2.6).fill({ color: COLOR.giltDark });
    g.circle(bx - 0.8, 8.2, 1.2).fill({ color: COLOR.giltLit, alpha: 0.85 });
  }
  // Gilt lip along the bottom, with thickness, then the shadow it throws down
  // onto the battlefield: the band is in front of the field, not painted on it.
  g.rect(x, h - 5, w, 5).fill({ color: COLOR.giltDark });
  g.rect(x, h - 3, w, 2).fill({ color: COLOR.gilt });
  g.rect(x, h - 1, w, 1).fill({ color: COLOR.giltLit });
  g.rect(x, h - 5.8, w, 1.6).fill({ color: 0x000000, alpha: 0.55 });
  for (let i = 0; i < 10; i++) {
    g.rect(x, h + i * 1.6, w, 1.6).fill({ color: 0x000000, alpha: 0.08 * (1 - i / 10) });
  }
  return g;
}

/**
 * The charge on an army's shield: **whose** army it is, not which end of the
 * field it started on.
 *
 * The old signature took `side` and branched on it, so the plaque captioned
 * `Gravewatch` flew a gold keep - the castle's own arms - because Gravewatch
 * happened to be defending, and the plaque captioned `Castle` flew crossed
 * swords because Castle happened to be attacking. Heraldry that changes with the
 * seating is not heraldry. Every faction has its own device now, and the same
 * army carries the same shield whichever side of the field it is standing on.
 */
export type ChargeKind =
  | "keep" | "tree" | "star" | "flame" | "skull" | "eye" | "swords"
  | "fangs" | "bolt" | "anchor" | "gear" | "peak" | "cross";

export const FACTION_CHARGE: Readonly<Record<string, ChargeKind>> = {
  sunspire: "keep",
  greenmantle: "tree",
  skyreach: "star",
  emberpit: "flame",
  gravewatch: "skull",
  deepvault: "eye",
  ironhorde: "swords",
  fenmire: "fangs",
  prismgate: "bolt",
  saltreach: "anchor",
  cogworks: "gear",
  frostmark: "peak",
  neutral: "cross",
};

export const chargeFor = (faction: string): ChargeKind => FACTION_CHARGE[faction] ?? "cross";

/**
 * An army's standard: a heraldic shield in the owner's colour, charged with the
 * faction's own device.
 *
 * It is the heraldic tag pinned to the commander's portrait at the end of the
 * console. The charge is drawn twice - a dark under-stroke then a gilt one a
 * pixel above it - because a single flat stroke on a coloured field disappears
 * the moment the field is dark, and half of these fields are.
 */
export function paintStandard(
  g: Graphics, w: number, h: number, tint: number, charge: ChargeKind,
): Graphics {
  g.clear();
  const shield = (): Graphics => g.moveTo(0, 0).lineTo(w, 0).lineTo(w, h * 0.5)
    .quadraticCurveTo(w * 0.92, h * 0.92, w / 2, h)
    .quadraticCurveTo(w * 0.08, h * 0.92, 0, h * 0.5)
    .closePath();
  shield().fill({ color: mixColor(tint, 0x1a1208, 0.28) });
  // Field: lit at the top, in shadow toward the base.
  for (let i = 0; i < 7; i++) {
    const k = i / 7;
    g.rect(0, h * k, w, h / 7 + 0.5)
      .fill({ color: k < 0.5 ? 0xffffff : 0x000000, alpha: Math.abs(0.42 - k) * 0.24 });
  }
  shield().stroke({ width: 2, color: COLOR.giltDark, alpha: 1, alignment: 0.5 });
  g.moveTo(1.5, 1.5).lineTo(w - 1.5, 1.5).stroke({ width: 1, color: COLOR.giltLit, alpha: 0.8 });

  const cy = h * 0.46;
  // Every charge is drawn as a pure function of (colour, alpha, dy) so the same
  // call can lay a shadow under it and the gilt over the top.
  const draw = (colour: number, alpha: number, dy: number): void => {
    paintCharge(g, charge, w, h, cy + dy, colour, alpha);
  };
  draw(0x1c1206, 0.85, 1);
  draw(COLOR.giltLit, 1, 0);
  return g;
}

/** One faction device, centred on `cy` within a `w` x `h` shield. */
function paintCharge(
  g: Graphics, charge: ChargeKind, w: number, h: number, cy: number,
  colour: number, alpha: number,
): void {
  const st = {
    width: Math.max(1.2, w * 0.09), color: colour, alpha,
    cap: "round" as const, join: "round" as const,
  };
  const thin = { ...st, width: st.width * 0.7 };
  const cx = w / 2;
  const u = Math.min(w, h * 0.72) * 0.5;
  switch (charge) {
    case "keep": {
      // Two towers and a curtain wall with a gate.
      const bw = u * 1.1, bx = cx - bw / 2;
      g.rect(bx, cy - u * 0.2, bw, u * 0.86).fill({ color: colour, alpha });
      for (const tx of [bx - u * 0.34, bx + bw - u * 0.06]) {
        g.rect(tx, cy - u * 0.72, u * 0.4, u * 1.38).fill({ color: colour, alpha });
      }
      g.rect(cx - u * 0.16, cy + u * 0.28, u * 0.32, u * 0.38).fill({ color: 0x140d05, alpha: alpha * 0.9 });
      break;
    }
    case "tree": {
      g.moveTo(cx, cy + u * 0.94).lineTo(cx, cy - u * 0.2).stroke(st);
      for (let i = 0; i < 3; i++) {
        const k = i / 3;
        const sw = u * (0.94 - k * 0.28);
        const sy = cy + u * (0.34 - k * 0.62);
        g.poly([cx - sw, sy, cx + sw, sy, cx, sy - u * 0.62]).fill({ color: colour, alpha });
      }
      break;
    }
    case "star": {
      for (let i = 0; i < 3; i++) {
        const ang = (Math.PI / 3) * i - Math.PI / 2;
        g.moveTo(cx - Math.cos(ang) * u, cy - Math.sin(ang) * u)
          .lineTo(cx + Math.cos(ang) * u, cy + Math.sin(ang) * u).stroke(st);
      }
      break;
    }
    case "flame": {
      g.moveTo(cx, cy - u)
        .quadraticCurveTo(cx + u * 0.86, cy - u * 0.1, cx + u * 0.42, cy + u * 0.86)
        .lineTo(cx - u * 0.42, cy + u * 0.86)
        .quadraticCurveTo(cx - u * 0.86, cy - u * 0.1, cx, cy - u)
        .closePath().fill({ color: colour, alpha });
      g.moveTo(cx, cy - u * 0.1)
        .quadraticCurveTo(cx + u * 0.3, cy + u * 0.3, cx, cy + u * 0.7)
        .quadraticCurveTo(cx - u * 0.3, cy + u * 0.3, cx, cy - u * 0.1)
        .closePath().fill({ color: 0x140d05, alpha: alpha * 0.55 });
      break;
    }
    case "skull": {
      g.ellipse(cx, cy - u * 0.14, u * 0.72, u * 0.72).fill({ color: colour, alpha });
      g.rect(cx - u * 0.34, cy + u * 0.34, u * 0.68, u * 0.44).fill({ color: colour, alpha });
      for (const dx of [-u * 0.3, u * 0.3]) {
        g.ellipse(cx + dx, cy - u * 0.2, u * 0.2, u * 0.24).fill({ color: 0x140d05, alpha: alpha * 0.95 });
      }
      g.rect(cx - u * 0.08, cy + u * 0.06, u * 0.16, u * 0.2).fill({ color: 0x140d05, alpha: alpha * 0.9 });
      break;
    }
    case "eye": {
      g.moveTo(cx - u, cy)
        .quadraticCurveTo(cx, cy - u * 0.94, cx + u, cy)
        .quadraticCurveTo(cx, cy + u * 0.94, cx - u, cy)
        .closePath().stroke(st);
      g.ellipse(cx, cy, u * 0.34, u * 0.34).fill({ color: colour, alpha });
      break;
    }
    case "swords": {
      for (const dir of [1, -1]) {
        g.moveTo(cx - dir * u * 0.8, cy + u * 0.72).lineTo(cx + dir * u * 0.8, cy - u * 0.76).stroke(st);
      }
      g.rect(cx - u * 0.6, cy + u * 0.22, u * 1.2, st.width).fill({ color: colour, alpha });
      break;
    }
    case "fangs": {
      // Three teeth over a jaw line: the swamp's own device.
      for (const dx of [-u * 0.6, 0, u * 0.6]) {
        g.poly([cx + dx - u * 0.26, cy - u * 0.5, cx + dx + u * 0.26, cy - u * 0.5, cx + dx, cy + u * 0.62])
          .fill({ color: colour, alpha });
      }
      g.moveTo(cx - u, cy - u * 0.56).lineTo(cx + u, cy - u * 0.56).stroke(thin);
      break;
    }
    case "bolt": {
      g.poly([
        cx + u * 0.32, cy - u, cx - u * 0.56, cy + u * 0.14,
        cx - u * 0.06, cy + u * 0.14, cx - u * 0.32, cy + u,
        cx + u * 0.6, cy - u * 0.2, cx + u * 0.08, cy - u * 0.2,
      ]).fill({ color: colour, alpha });
      break;
    }
    case "anchor": {
      g.moveTo(cx, cy - u * 0.62).lineTo(cx, cy + u * 0.82).stroke(st);
      g.moveTo(cx - u * 0.46, cy - u * 0.34).lineTo(cx + u * 0.46, cy - u * 0.34).stroke(thin);
      g.circle(cx, cy - u * 0.78, u * 0.22).stroke(thin);
      g.moveTo(cx - u * 0.78, cy + u * 0.28)
        .quadraticCurveTo(cx, cy + u * 1.12, cx + u * 0.78, cy + u * 0.28).stroke(st);
      break;
    }
    case "gear": {
      g.circle(cx, cy, u * 0.56).stroke(st);
      for (let i = 0; i < 8; i++) {
        const ang = (Math.PI / 4) * i;
        g.moveTo(cx + Math.cos(ang) * u * 0.56, cy + Math.sin(ang) * u * 0.56)
          .lineTo(cx + Math.cos(ang) * u, cy + Math.sin(ang) * u).stroke(thin);
      }
      break;
    }
    case "peak": {
      g.poly([cx - u, cy + u * 0.7, cx - u * 0.2, cy - u * 0.86, cx + u * 0.34, cy - u * 0.1,
        cx + u * 0.58, cy - u * 0.5, cx + u, cy + u * 0.7]).fill({ color: colour, alpha });
      g.poly([cx - u * 0.2, cy - u * 0.86, cx - u * 0.52, cy - u * 0.1, cx + u * 0.1, cy - u * 0.1])
        .fill({ color: 0xf6ecd6, alpha: alpha * 0.5 });
      break;
    }
    default: {
      // A plain cross: the neutral standard, and the answer for a faction this
      // table has never heard of - never a silently blank shield.
      g.rect(cx - u * 0.2, cy - u, u * 0.4, u * 2).fill({ color: colour, alpha });
      g.rect(cx - u * 0.86, cy - u * 0.2, u * 1.72, u * 0.4).fill({ color: colour, alpha });
      break;
    }
  }
}

// ------------------------------------------------------------ count plate

/**
 * The stack-count plate, the way the classic paints it: a small purple field with a
 * cream keyline, and the field turns red while the stack is under a harmful
 * effect.
 *
 * Measured on a reference screenshot
 * (native 800x600, three plates by connected components of the purple fill):
 * the fill is 28 x 9 and runs left to right from (64,21,107) to (99,33,165) -
 * a horizontal gradient, darker at the hoist - inside a one-pixel keyline of
 * (255,231,132). The numeral is white. The plate is the same purple on both
 * armies; the classic says whose stack it is with the creature's facing and the
 * field's own furniture, not with the plate. It used to be the owner's colour
 * here, which read well and read wrong: no frame of the reference set has a
 * red plate on one side and a blue one on the other.
 *
 * The red is the harmful-effect plate (`CMNUMWINR` in the game's own data),
 * used here for the three statuses the kernel carries as flags - blinded,
 * petrified, blood_rage - which are the ones a player has to see at a glance.
 */
export const COUNT_PLATE = {
  hoist: 0x40156b,
  fly: 0x6321a5,
  keyline: 0xffe784,
  numeral: 0xffffff,
  harmHoist: 0x6b1515,
  harmFly: 0xa52121,
} as const;

/** The plate's two field tones for a stack: harmful effects turn it red. */
export function countPlateTones(harmed: boolean): { readonly hoist: number; readonly fly: number } {
  return harmed
    ? { hoist: COUNT_PLATE.harmHoist, fly: COUNT_PLATE.harmFly }
    : { hoist: COUNT_PLATE.hoist, fly: COUNT_PLATE.fly };
}
